/* eslint-disable filename-rules/match */

import { useMemo } from "react";

import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectCourseUsersForCurrentUser, selectCurrentUserId } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { useLocation } from "utils/routing";
import { HomeRoleType, homeUrlForRole } from "utils/urls";

export const getDefaultHomeRole = (canAuthorOrg: boolean, isStaffInAnyCourse: boolean) => {
  return canAuthorOrg ? "admin" : isStaffInAnyCourse ? "instructor" : "student";
};

export const useHomeRole = () => {
  const location = useLocation();
  const userId = useAppSelector(selectCurrentUserId);

  // We need to load course and org users to determine roles
  // Skip API calls if userId is undefined (user not logged in)
  const { isLoading } = usePromiseEffect(() => {
    if (!userId) return Promise.resolve(undefined);

    return Promise.all([
      storeApi.course_users.list({ user_id: userId }),
      storeApi.org_users.list({ user_id: userId }),
    ]);
  }, [userId]);

  const { canAuthorOrg } = useCapabilities();
  const courseUsers = useAppSelector(selectCourseUsersForCurrentUser);
  const isStaffInAnyCourse = courseUsers.some((cu) => cu.role !== "student");
  const isStudentInAnyCourse = courseUsers.some((cu) => cu.role === "student");

  const availableRoles: HomeRoleType[] = useMemo(
    () =>
      [
        canAuthorOrg && "admin",
        isStaffInAnyCourse && "instructor",
        isStudentInAnyCourse && "student",
      ].filter(Boolean) as HomeRoleType[],
    [canAuthorOrg, isStaffInAnyCourse, isStudentInAnyCourse],
  );

  const currentRole = availableRoles.find((role) =>
    location.pathname.startsWith(homeUrlForRole(role)),
  );

  return { currentRole, availableRoles, isLoading };
};
